import React, { useEffect, useState, useMemo } from "react";
import { Table, Row, Col, message, Space, Button } from "antd";
import {
    fetchCatalogues,
    fetchProducts,
    fetchVariants,
} from "../../services/api/gatewaysApi";
import { processCatalogueData } from "../../services/data/gatewaysData";
import SummaryCard from "../Analytics/SummaryCard";
import ColorIndicator from "../Analytics/ColorIndicator";
import SummaryGrid from "../Analytics/SummaryGrid";
import { ReloadOutlined, InfoCircleOutlined } from "@ant-design/icons";
import CustomTooltip from "../Components/Modals/CustomTooltip";
import { variantTokens, primaryShades } from "../../theme/theme";
import EmptyCard from "../EmptyCard";
import { formatNumberBritish } from "../../utils/numberFormatting";
import LoadingCard from "../Components/Cards/LoadingCard";

const GlassesVariantsAnalytics = ({ data, loading, clearFiltersVisible, onResetFilters }) => {
    const [messageApi, contextHolder] = message.useMessage();
    const [analyticsData, setAnalyticsData] = useState({
        brands: [],
        products: [],
        variants: [],
    });

    // Stato per tenere traccia del prodotto selezionato
    const [selectedProduct, setSelectedProduct] = useState(null);
    // Stato per mostrare prodotti/varianti filtrati
    const [filteredData, setFilteredData] = useState({
        products: [],
        variants: []
    });

    const [selectedTable, setSelectedTable] = useState("Products");

    // Testi informativi per i tooltip delle card
    const tooltipTextMap = {
        "Products": "Number of different products tried on by users",
        "Variants": "Number of different product variants tried on by users"
    };

    // Calcola i dati da visualizzare in base ai filtri e alle selezioni
    const displayData = useMemo(() => {
        // Se c'è un prodotto selezionato, usiamo i dati filtrati
        if (selectedProduct) {
            return {
                products: filteredData.products,
                variants: filteredData.variants
            };
        }
        // Altrimenti usiamo tutti i dati
        return analyticsData;
    }, [selectedProduct, filteredData, analyticsData]);

    // Calcoliamo i conteggi per le card summary in modo simile ad AnalyticsTable
    const productCount = displayData.products.filter(p => p.id !== 'total').length;
    const variantCount = displayData.variants.filter(v => v.id !== 'total').length;

    const summaryData = [
        { title: "Products", count: productCount },
        { title: "Variants", count: variantCount },
    ];

    const formatTime = (seconds) => {
        if (!seconds || isNaN(seconds)) return "00:00:00";

        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = Math.floor(seconds % 60);
        return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
            2,
            "0"
        )}:${String(secs).padStart(2, "0")}`;
    };

    useEffect(() => {
        const buildDataStructure = async () => {
            try {
                const processedData = processCatalogueData(data);

                if (processedData.length === 0) {
                    setAnalyticsData({
                        brands: [],
                        products: [],
                        variants: [],
                    });
                    return;
                }

                const allProductIds = processedData.flatMap((item) =>
                    item.products.map((product) => product.id)
                );
                const allVariantIds = processedData.flatMap((item) =>
                    item.variants.map((variant) => variant.id)
                );
                const uniqueBrands = processedData.map(
                    (item) => item.catalogueRef
                );

                const brands = await fetchCatalogues(uniqueBrands);
                const productsDetails = await fetchProducts(allProductIds);
                const variantsDetails = await fetchVariants(allVariantIds);

                // Crea un mapping di prodotti e varianti per collegare i prodotti alle loro varianti
                const productToVariantsMap = {};

                // Se nella risposta API è disponibile l'informazione di quale prodotto appartiene una variante
                variantsDetails.forEach(variant => {
                    if (variant.productId || variant.productRef || variant.refProduct) {
                        const productId = variant.productId || variant.productRef || variant.refProduct;
                        if (!productToVariantsMap[productId]) {
                            productToVariantsMap[productId] = [];
                        }
                        productToVariantsMap[productId].push(variant.idVariant || variant.id);
                    }
                });

                // Calcola i totali per i prodotti
                let totalProductVisualization = 0;
                let totalProductTime = 0;

                // Costruiamo un indice per tracciare le occorrenze dei prodotti
                const productOccurrencesMap = {};

                const enrichedProducts = productsDetails.map((product) => {
                    const matchingProduct = processedData
                        .flatMap((item) => item.products)
                        .find((g) => g.id === product.idProduct);

                    const visualizations = matchingProduct ? matchingProduct.visualization : 0;
                    const time = matchingProduct ? matchingProduct.visualizationTime : 0;

                    // Aggiorna i totali
                    totalProductVisualization += visualizations;
                    totalProductTime += time;

                    // Traccia le occorrenze di questo prodotto
                    if (!productOccurrencesMap[product.idProduct || product.id]) {
                        productOccurrencesMap[product.idProduct || product.id] = 0;
                    }
                    productOccurrencesMap[product.idProduct || product.id]++;

                    return {
                        ...product,
                        id: product.idProduct || product.id,
                        name: product.nameModel, // Assicuriamo che il nome sia impostato qui
                        visualizations: visualizations,
                        time: time,
                        // Aggiungiamo le varianti associate, se disponibili
                        variantIds: productToVariantsMap[product.idProduct || product.id] || []
                    };
                });

                // Filtra eventuali prodotti duplicati, mantenendo solo la prima occorrenza
                const uniqueProducts = enrichedProducts.filter((product, index, self) => {
                    // Se è la prima occorrenza di questo ID nel array, tienilo
                    return index === self.findIndex(p => p.id === product.id);
                });

                // Costruiamo un indice per tracciare le occorrenze delle varianti
                const variantOccurrencesMap = {};

                // Assicuriamo che i totali delle varianti e dei prodotti siano allineati
                const enrichedVariants = variantsDetails.map((variant) => {
                    const matchingVariant = processedData
                        .flatMap((item) => item.variants)
                        .find((v) => v.id === variant.idVariant);

                    // Determina a quale prodotto appartiene questa variante
                    let productId = variant.productId || variant.productRef || variant.refProduct;

                    // Se non c'è un riferimento diretto, cerca in productToVariantsMap
                    if (!productId) {
                        for (const [pid, variantIds] of Object.entries(productToVariantsMap)) {
                            if (variantIds.includes(variant.idVariant || variant.id)) {
                                productId = pid;
                                break;
                            }
                        }
                    }

                    // Traccia le occorrenze di questa variante
                    const variantId = variant.idVariant || variant.id;
                    if (!variantOccurrencesMap[variantId]) {
                        variantOccurrencesMap[variantId] = 0;
                    }
                    variantOccurrencesMap[variantId]++;

                    const visualizations = matchingVariant ? matchingVariant.visualization : 0;
                    const time = matchingVariant ? matchingVariant.visualizationTime : 0;

                    return {
                        ...variant,
                        id: variantId,
                        name: variant.nameVariant, // Assicuriamo che il nome sia impostato qui
                        visualizations: visualizations,
                        time: time,
                        productId, // Associa al prodotto
                        sku: variant.skuModel || variant.sku || variant.modelSku || '',
                        frameColor: variant.frameColor || variant.colorFrame || variant.primaryFrameColor || ''
                    };
                });

                // Filtra eventuali varianti duplicate, mantenendo solo la prima occorrenza
                const uniqueVariants = enrichedVariants.filter((variant, index, self) => {
                    // Se è la prima occorrenza di questo ID nel array, tienilo
                    return index === self.findIndex(v => v.id === variant.id);
                });

                // Ricalcola i totali usando l'approccio dei dati originali
                // Questo garantisce che i totali siano esattamente gli stessi per prodotti e varianti
                let totalVisualization = 0;
                let totalTime = 0;

                // Usa i dati originali da processedData che contiene già i conteggi corretti
                processedData.forEach(item => {
                    item.products.forEach(product => {
                        totalVisualization += product.visualization || 0;
                        totalTime += product.visualizationTime || 0;
                    });
                });

                // Crea oggetti "total" usando i totali calcolati
                const productsWithTotal = [
                    {
                        id: 'total',
                        name: 'Total',
                        visualizations: totalVisualization,
                        time: totalTime
                    },
                    ...uniqueProducts
                ];

                // Usa gli stessi totali per le varianti - questo garantisce che siano identici
                const variantsWithTotal = [
                    {
                        id: 'total',
                        name: 'Total',
                        visualizations: totalVisualization,
                        time: totalTime
                    },
                    ...uniqueVariants
                ];

                setAnalyticsData({
                    brands,
                    products: productsWithTotal,
                    variants: variantsWithTotal,
                });
            } catch (error) {
                console.error("Error loading data:", error);
                messageApi.error(`Error loading data: ${error.message}`);
            }
        };

        buildDataStructure();
    }, [data, messageApi]);

    // Quando il prodotto selezionato cambia, aggiorniamo i dati filtrati
    useEffect(() => {
        if (selectedProduct) {
            // Filtro solo il prodotto selezionato (più il totale)
            const selectedProductObj = analyticsData.products.find(p => p.id === selectedProduct.id);

            if (selectedProductObj) {
                // Creiamo un nuovo totale specifico per il prodotto selezionato
                const productTotal = {
                    id: 'total',
                    name: 'Total',
                    visualizations: selectedProductObj.visualizations,
                    time: selectedProductObj.time
                };

                const filteredProducts = [productTotal, selectedProductObj];

                // Filtra le varianti per il prodotto selezionato e rimuovi eventuali duplicati
                const variantsForProduct = [...new Set(
                    analyticsData.variants.filter(variant =>
                        variant.id !== 'total' &&
                        (variant.productId === selectedProductObj.id ||
                            selectedProductObj.variantIds?.includes(variant.id))
                    )
                )];

                // Filtro per rimuovere varianti che hanno lo stesso id (ancora più sicurezza)
                const uniqueVariantsForProduct = [];
                const variantIds = new Set();

                for (const variant of variantsForProduct) {
                    if (!variantIds.has(variant.id)) {
                        variantIds.add(variant.id);
                        uniqueVariantsForProduct.push(variant);
                    }
                }

                // Creare un totale per le varianti filtrate
                const variantTotal = {
                    id: 'total',
                    name: 'Total',
                    visualizations: uniqueVariantsForProduct.reduce((sum, v) => sum + v.visualizations, 0),
                    time: uniqueVariantsForProduct.reduce((sum, v) => sum + v.time, 0)
                };

                // Aggiorna lo stato con i dati filtrati
                setFilteredData({
                    products: filteredProducts,
                    variants: [variantTotal, ...uniqueVariantsForProduct]
                });
            }
        } else {
            // Reset dati filtrati se nessun prodotto è selezionato
            setFilteredData({
                products: [],
                variants: []
            });
        }
    }, [selectedProduct, analyticsData]);

    const handleCardClick = (type) => {
        setSelectedTable(type);
    };

    // Gestore per la selezione di un prodotto
    const handleProductSelect = (product) => {
        // Ignora il click sulla riga del totale
        if (product.id === 'total') return;

        setSelectedProduct(product);
        // La card dei Products resta selezionata
    };

    // Funzione per resettare la selezione del prodotto
    const handleResetSelection = () => {
        setSelectedProduct(null);
    };

    const renderTable = () => {
        const tooltipTextMap = {
            Products: "The sum of time that the users spent trying-on a product",
            Variants: "The sum of time that the users spent trying-on a variant",
        };

        // Verifica se ci sono dati da visualizzare
        const hasData = (dataType) => {
            return displayData[dataType.toLowerCase()]?.filter(item => item.id !== 'total').length > 0;
        };

        // Se non ci sono dati per il tipo selezionato, mostra EmptyCard
        if (!hasData(selectedTable)) {
            return <EmptyCard />;
        }

        const commonColumns = [
            {
                title: (
                    <Row justify="space-between" align="middle" style={{ width: "100%" }}>
                        <Col>
                            <Space size="small">
                                <span>{selectedTable}</span>
                                <CustomTooltip title={tooltipTextMap[selectedTable]}>
                                    <InfoCircleOutlined style={{ fontSize: '14px', color: 'inherit' }} />
                                </CustomTooltip>
                            </Space>
                        </Col>
                    </Row>
                ),
                dataIndex: "name",
                key: "name",
                sorter: (a, b) => {
                    const aName = typeof a.name === 'string' ? a.name : 'Unnamed';
                    const bName = typeof b.name === 'string' ? b.name : 'Unnamed';
                    return aName.localeCompare(bName);
                },
                width: selectedTable === "Variants" ? "25%" : "40%",
                showSorterTooltip: false,
                render: (_, record) => {
                    if (selectedTable === "Variants") {
                        return (
                            <Space size="small" align="center">
                                <span>{record.name}</span>
                                {record.esaColorFramePrimary && (
                                    <ColorIndicator
                                        primary={record.esaColorFramePrimary}
                                        secondary={record.esaColorFrameSecondary}
                                        type="square"
                                        size="small"
                                        title="Frame color"
                                    />
                                )}
                                {record.esaColorLensesPrimary && (
                                    <ColorIndicator
                                        primary={record.esaColorLensesPrimary}
                                        secondary={record.esaColorLensesSecondary}
                                        size="small"
                                        title="Lens color"
                                    />
                                )}
                            </Space>
                        );
                    }
                    return record.name;
                },
            },
            // Colonne SKU e Frame Color solo per le varianti
            ...(selectedTable === "Variants" ? [
                {
                    title: (
                        <Space size="small">
                            <span>SKU</span>
                            <CustomTooltip title="Stock Keeping Unit - Unique identifier for each product variant">
                                <InfoCircleOutlined style={{ fontSize: '14px', color: 'inherit' }} />
                            </CustomTooltip>
                        </Space>
                    ),
                    dataIndex: "sku",
                    key: "sku",
                    width: "15%",
                    render: (text, record) => {
                        if (record.id === 'total') return '';
                        return text || '-';
                    },
                    sorter: (a, b) => {
                        const aValue = a.sku || '';
                        const bValue = b.sku || '';
                        return aValue.localeCompare(bValue);
                    },
                    showSorterTooltip: false,
                },
                {
                    title: (
                        <Space size="small">
                            <span>Frame Color</span>
                            <CustomTooltip title="Color of the frame for this variant">
                                <InfoCircleOutlined style={{ fontSize: '14px', color: 'inherit' }} />
                            </CustomTooltip>
                        </Space>
                    ),
                    dataIndex: "frameColor",
                    key: "frameColor",
                    width: "15%",
                    render: (text, record) => {
                        if (record.id === 'total') return '';
                        return text || '-';
                    },
                    sorter: (a, b) => {
                        const aValue = a.frameColor || '';
                        const bValue = b.frameColor || '';
                        return aValue.localeCompare(bValue);
                    },
                    showSorterTooltip: false,
                },
            ] : []),
            {
                title: (total) => {
                    const totalVisualization = total?.visualizations || 0;
                    return (
                        <span style={{ textAlign: 'right', display: 'block' }}>
                            <Space size="small">
                                <span>Visualizations</span>
                                <CustomTooltip title="Number of times this item has been viewed or tried on">
                                    <InfoCircleOutlined style={{ fontSize: '14px', color: 'inherit' }} />
                                </CustomTooltip>
                            </Space>
                            {total && (
                                <>
                                    <span style={{ fontWeight: 300 }}> - Total </span>
                                    <span>{formatNumberBritish(totalVisualization, 0)}</span>
                                </>
                            )}
                        </span>
                    );
                },
                dataIndex: "visualizations",
                key: "visualizations",
                sorter: (a, b) => a.visualizations - b.visualizations,
                defaultSortOrder: "descend",
                showSorterTooltip: false,
                width: selectedTable === "Variants" ? "22.5%" : "30%",
                align: 'right',
                render: (value) => formatNumberBritish(value, 0)
            },
            {
                title: (total) => {
                    const totalTime = formatTime(total?.time || 0);
                    return (
                        <span style={{ textAlign: 'right', display: 'block' }}>
                            <Space size="small">
                                <span>Time</span>
                                <CustomTooltip title="Total time spent viewing or trying on this item">
                                    <InfoCircleOutlined style={{ fontSize: '14px', color: 'inherit' }} />
                                </CustomTooltip>
                            </Space>
                            {total && (
                                <>
                                    <span style={{ fontWeight: 300 }}> - Total Time Spent </span>
                                    <span>{totalTime}</span>
                                </>
                            )}
                        </span>
                    );
                },
                dataIndex: "time",
                key: "time",
                sorter: (a, b) => a.time - b.time,
                showSorterTooltip: false,
                width: selectedTable === "Variants" ? "22.5%" : "30%",
                align: 'right',
                render: (time) => formatTime(time)
            },
        ];

        const paginationSettings = {
            defaultPageSize: 10,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100", "200", "500", "1000"],
        };

        // Trova il record totale per passarlo alle intestazioni delle colonne
        const totalRecord = displayData[selectedTable.toLowerCase()].find(r => r.id === 'total');

        // Adatta le colonne con i totali
        const columnsWithTotals = selectedTable === "Variants" ? [
            commonColumns[0], // Name
            commonColumns[1], // SKU
            commonColumns[2], // Frame Color
            {
                ...commonColumns[3],
                title: commonColumns[3].title(totalRecord),
            }, // Visualizations
            {
                ...commonColumns[4],
                title: commonColumns[4].title(totalRecord),
            }, // Time
        ] : [
            commonColumns[0],
            {
                ...commonColumns[1],
                title: commonColumns[1].title(totalRecord),
            },
            {
                ...commonColumns[2],
                title: commonColumns[2].title(totalRecord),
            },
        ];

        // Renderizza il bottone di reset filters
        const renderResetButton = () => {
            if (selectedProduct) {
                return (
                    <div className="reset-filter-container">
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={handleResetSelection}
                            type="primary"
                            size="small"
                        >
                            Reset filters
                        </Button>
                    </div>
                );
            }
            return null;
        };

        if (selectedTable === "Products") {
            return (
                <div className="table-container">
                    <Table
                        dataSource={displayData.products
                            .filter(product => product.id !== 'total') // Rimuovi la riga del totale
                            .map((product, index) => {
                                // Trovo tutte le possibili proprietà contenenti il nome
                                const possibleNames = [
                                    product.name,
                                    product.modelName,
                                    product.nameModel,
                                    product.nome_modello,
                                    product.glassesName
                                ].filter(Boolean);

                                return {
                                    ...product,
                                    key: `product-${product.id}-${index}`, // Genera chiave unica combinando ID e indice
                                    name: possibleNames.length > 0 ? possibleNames[0] : "Unnamed",
                                };
                            })}
                        rowKey="key" // Usa il campo key invece di id
                        columns={columnsWithTotals}
                        pagination={paginationSettings}
                        size="small"
                        scroll={{ x: "max-content" }}
                        onRow={(record) => ({
                            onClick: () => handleProductSelect(record),
                            style: {
                                cursor: 'pointer',
                                background: selectedProduct && record.id === selectedProduct.id ? '#e6f7ff' : 'inherit'
                            }
                        })}
                        className="analytics-table"
                    />
                    {renderResetButton()}
                </div>
            );
        } else if (selectedTable === "Variants") {
            return (
                <div className="table-container">
                    <Table
                        dataSource={displayData.variants
                            .filter(variant => variant.id !== 'total') // Rimuovi la riga del totale
                            .map((variant, index) => {
                                // Trovo tutte le possibili proprietà contenenti il nome
                                const possibleNames = [
                                    variant.name,
                                    variant.glassesName,
                                    variant.nameVariant
                                ].filter(Boolean);

                                return {
                                    ...variant,
                                    key: `variant-${variant.id}-${index}`, // Genera chiave unica combinando ID e indice
                                    name: possibleNames.length > 0 ? possibleNames[0] : "Unnamed",
                                    esaColorFramePrimary: variant.primaryFrameColorHex || variant.esaColorFramePrimary,
                                    esaColorFrameSecondary: variant.secondaryFrameColorHex || variant.esaColorFrameSecondary,
                                    esaColorLensesPrimary: variant.primaryLensesColorHex || variant.esaColorLensesPrimary,
                                    esaColorLensesSecondary: variant.secondaryLensesColorHex || variant.esaColorLensesSecondary,
                                };
                            })}
                        rowKey="key" // Usa il campo key invece di id
                        columns={columnsWithTotals}
                        pagination={paginationSettings}
                        size="small"
                        scroll={{ x: "max-content" }}
                        className="analytics-table"
                    />
                    {renderResetButton()}
                </div>
            );
        }
        return null;
    };

    return (
        <>
            {contextHolder}
            <SummaryGrid>
                {summaryData.map(({ title, count }) => (
                    <SummaryCard
                        key={title}
                        label={title}
                        value={count}
                        loading={loading}
                        active={title === selectedTable}
                        onClick={() => handleCardClick(title)}
                        toolTipText={tooltipTextMap[title]}
                        index={title === "Products" ? 0 : 1}
                        totalitems={2}
                    />
                ))}
            </SummaryGrid>
            {loading ? (
                <LoadingCard />
            ) : (
                <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
                    <Col span={24}>{renderTable()}</Col>
                </Row>
            )}
            <style dangerouslySetInnerHTML={{
                __html: `
                    .total-row {
                        color: #888;
                        font-weight: bold;
                    }
                    .analytics-table .ant-pagination-item {
                        background: transparent;
                        border: none;
                    }
                    .analytics-table .ant-pagination-item-active {
                        background: ${primaryShades.primary300};
                    }
                    .analytics-table .ant-pagination-item-active a {
                        color: ${variantTokens.white};
                    }
                    .table-container {
                        border: 1px solid #d9d9d9;
                        border-radius: 8px;
                        overflow: hidden;
                        position: relative;
                    }
                    .analytics-table .ant-pagination {
                        padding-right: 8px;
                    }
                    .reset-filter-container {
                        position: absolute;
                        bottom: 16px;
                        left: 16px;
                        z-index: 1;
                    }
                `
            }} />
        </>
    );
};

export default GlassesVariantsAnalytics;